import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "Why you can't find an engineering coach",
  "description": "Wanna learn boxing? Join a gym, find a coach. Tennis player with a kink in your game? Find a coach. Software engineer with a missing skill? Google, random blogs, fun newsletters, dubious podcasts, noise on twitter 🤨",
  "published": "2021-01-08T00:00:00.000Z",
  "categories": "Senior Mindset",
  "content_upgrade": "SeniorMindset",
  "hero": "./img/screenshot-1610127042789.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`Wanna learn boxing? Join a gym, find a coach.`}</p>
    <p>{`Tennis player with a kink in your game? Find a coach. Need stamina? Coach. Strength? Coach. Technique? Coach.`}</p>
    <p>{`Business owner with a tricky problem? Find a coach. Human with issues? Find a `}<del parentName="p">{`therapist`}</del>{` coach. Procrastinator? Productivity coach. Writer? Writing coach.`}</p>
    <p>{`Software engineer with a missing skill? Google, random blogs, fun newsletters, dubious podcasts, noise on twitter, $10 courses on Udemy that you'll never open ... wait what? 🤨`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/3LLtumhkpIW8U/giphy-loop.mp4?cid=7cf28b0fo33ii9jzwkd1n7i976w9fnm7e8vvp8ts5t47vac9&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "who-hires-a-coach"
    }}>{`Who hires a coach`}</h2>
    <blockquote>
      <p parentName="blockquote">{`You hire a coach when you want the result, not the process. Eyes on the prize.`}</p>
    </blockquote>
    <p>{`I run marathons. There is no reward. It's a way to read audiobooks, challenge yourself, and get off the computer. I'm not good enough to compete for prizes.`}</p>
    <p>{`No coach required. Read articles, experiment with training schedules, get technique pointers in the beginning so you don't injure yourself. Oh wait that's a coach.`}</p>
    <p>{`My sister ran track in high school, she helped 🥰`}</p>
    <p>{`Business, job, and indie hacking? Now there's a prize!`}</p>
    <p>{`You can go from 25k/year to 115k in 4 years. `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/what-i-learned-while-6xing-my-income-in-4-years"
      }}>{`Like I did from 2012 to 2016`}</a>{`. Up to about `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/reflecting-on-2020"
      }}>{`250k these days`}</a>{`.`}</p>
    <p>{`You bet your ass I bought all the courses and advice I could afford! This one time I even hired a coach for `}<em parentName="p">{`one`}</em>{` negotiation. Worth it 🤘`}</p>
    <ContentUpgrades.SeniorMindset />
    <h2 {...{
      "id": "where-are-the-engineering-coaches"
    }}>{`Where are the engineering coaches?`}</h2>
    <p>{`Who do you think becomes a coach?`}</p>
    <p>{`An expert who can't do it anymore. Too old, too injured, too tired.`}</p>
    <p>{`Sports careers have a deadline. Female gymnasts are done by 23. The oldest No. 1 tennis player was 35.`}</p>
    <p>{`Many retire before their deadline. Lack of `}{`[`}{`genetic] potential. Not worth the pain.`}</p>
    <p>{`Business folk grow tired of businessing. Their ideas lack traction, their drive is burnt out, the fresh kids better understand new markets. Or they got rich and bored.`}</p>
    <p>{`What do you do with all this expertise you can't use? Share. Teach. Coach. Lucrative business when done right.`}</p>
    <p><strong parentName="p">{`What happens to old engineers?`}</strong>{` They keep making bank and having fun at their job.`}</p>
    <p>{`There is no deadline, there is no limit. Always something new to learn. And compared to finding coaching clients `}<em parentName="p">{`it's so much easier`}</em>{`. Pays better too.`}</p>
    <p>{`But engineering coaches do exist! `}<em parentName="p">{`You`}</em>{` don't hire them, someone else does.`}</p>
    <h2 {...{
      "id": "coach-as-manager"
    }}>{`Coach as manager`}</h2>
    <p>{`You think of managers as task masters. The folk who tell you what to do.`}</p>
    <p>{`And that's true in many industries. But it doesn't work for engineering and other creative work.`}</p>
    <p>{`To manage creatives, you have to coach. Enable, empower, set a north star. Modern management.`}</p>
    <blockquote>
      <p parentName="blockquote">{`If you want to build a ship, don’t drum up the men to gather wood, divide the work and give orders. Instead, teach them to yearn for the vast and endless sea.`}</p>
    </blockquote>
    <p><strong parentName="p">{`Your manager is your coach.`}</strong></p>
    <p>{`A good technical manager is a shit umbrella and team coach. They offer advice, explore technical innovations, and listen to their team.`}</p>
    <p>{`When you need a challenge, they find it. When you want direction, they help. When you want growth, they push.`}</p>
    <p>{`That's a coach. You benefit, your employer pays.`}</p>
    <p><em parentName="p">{`PS: the good ones are former engineers who got bored of engineering ;)`}</em></p>
    <h2 {...{
      "id": "coach-as-mentor"
    }}>{`Coach as mentor`}</h2>
    <p>{`Mentoring is the best type of coaching for engineers.`}</p>
    <p>{`A senior team member takes you under their wing. The business feeds you and your mentor problems and challenges. Endless supply.`}</p>
    <p>{`Your mentor knows where you're at and filters challenges to match difficulty. Too easy and you're bored, too hard and you shy away.`}</p>
    <p>{`You want to be challenged and always making progress. Like a video game with adaptive difficulty.`}</p>
    <p>{`Stems from Mihaly Csikszentmihalyi's research on the psychology of flow. Google it, great read.`}</p>
    <p>{`Your mentor is there to help with questions, research, reading resources, and hands-on code. Always a slack or a nudge away, happy to help.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Yay did my first in-house training for my own team! 🥳`}<br parentName="p"></br><br parentName="p"></br>{`It's a little different than the public workshops I've done in the past 🧵 `}<a parentName="p" {...{
          "href": "https://t.co/s6YQiMoHyn"
        }}>{`pic.twitter.com/s6YQiMoHyn`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1347290503654490117"
      }}>{`January 7, 2021`}</a></blockquote>
    <p>{`Plus your mentor signed the same NDA and security clearance as you did. `}<strong parentName="p">{`You can share the code.`}</strong>{` This is where independent coaching struggles.`}</p>
    <p><strong parentName="p">{`AND`}</strong>{` your mentor can get in the code and make it better. Set up frameworks to make your life easier. 😍`}</p>
    <h2 {...{
      "id": "coach-as-consultant"
    }}>{`Coach as consultant`}</h2>
    <p>{`The last type of coaching, that I've seen work in the wild, are consultants. You don't hire them, your business does.`}</p>
    <p>{`Got a tricky problem your team can't solve fast enough? Hire an expert, get a workshop, tailored advice, or a detailed roadmap.`}</p>
    <p>{`Consultant parachutes into the team, gathers all the info, designs a solution, and bails. You get to do the work and it's easier with a map.`}</p>
    <p>{`Happy Friday ❤️`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      